import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useParams } from "react-router-dom";

import type { IBreadcrumbItem } from "@/src/common/Breadcrumb";
import type { IEntryPointComponent } from "@/src/common/PageTransition";
import { useQueryPageCustom } from "@/src/query-client-factory";
import PageLayout from "@/src/shared/page-layout";
import { AjaxMethod } from "@Components/Hooks/useAjax";
import type { ILink } from "@Components/Link";
import { Loading } from "@Components/Loading";
import { sendToGTMBrandDetails } from "@Components/Modules/Helpers";
import type { IPanel } from "@Components/Panel";
import { Panel } from "@Components/Panel";
import { ItemTemplate, NoResultsTemplate } from "@Components/Repeater";
import RepeaterV2 from "@Components/Repeater/RepeaterV2";
import type { ISearchBox, ISearchBoxResultItem } from "@Components/SearchBox";
import { SearchBox } from "@Components/SearchBox";
import { FollowedBrands } from "@Routes/BrandLinks";

import type { ICashbackDetails } from "../cashback/CashbackDetails";
import type { IActionButtonBanner } from "../common/ActionButtonBanner";
import type { IAppDownloadBanner } from "../common/AppDownloadBanner";
import AppDownloadBanner from "../common/AppDownloadBanner";
import type { IContentContainer } from "../common/ContentContainer";
import ContentContainer from "../common/ContentContainer";
import type { ILogoTile } from "../common/LogoTile";
import LogoTile from "../common/LogoTile";
import { TransitionLink } from "../common/Transition";
import type { IDiscountNavigation } from "../discount/DiscountNavigation";
import { DiscountNavigation } from "../discount/DiscountNavigation";
import type { IDiscountTile } from "../discount/DiscountTile";
import { NotFound } from "../pages/not-found";
import type { IFlyersSlider } from "../sponsored-brand/FlyersSlider";
import FlyersSlider from "../sponsored-brand/FlyersSlider";
import type { ISuggestedFlyers } from "../sponsored-brand/SuggestedFlyers";
import { SuggestedFlyers } from "../sponsored-brand/SuggestedFlyers";
import type { IBrandDetailsContent } from "./BrandDetailsContent";
import { BrandDetailsContent } from "./BrandDetailsContent";
import type { IBrandDetailsHeader } from "./BrandDetailsHeader";
import { BrandDetailsHeader } from "./BrandDetailsHeader";
import { PCPopularBrandOffers } from "./pc-popular-brand-offers";

import * as portalCss from "../shared/Portal.module.css";
import * as css from "./BrandDetails.module.css";

export interface IBrandDetails extends IEntryPointComponent {
    id: string;
    initialData: IBrandDetailsResult;
    actionButtons?: IActionButtonBanner;
    brandDetailsHeader: IBrandDetailsHeader;
    brandDetailsContent: IBrandDetailsContent;
    flyersList: IFlyersSlider;
    flyersTitle: string;
    allShopsLink: ILink;
    suggestedBrandsPanel: IPanel;
    appDownloadBanner: IAppDownloadBanner;
    brandProductsSearch: ISearchBox;
    suggestedFlyers: ISuggestedFlyers;
    contentContainers: IContentContainer;
    canonicalUrl: string;
    brandUrl: string;
    navigation?: IDiscountNavigation;
    hasSearchNavigation?: boolean;
    flyersNoResults?: string;
}

interface IBrandDetailsResult {
    suggestedBrands: ILogoTile[];
    isBrandFollowed: boolean;
    showNewsletterSignupItem: boolean;
    cashbackDetails: ICashbackDetails;
    discounts: IDiscountTile[];
}

interface IBrandDetailsState {
    searchText: string;
}

export const BrandDetails: FC = () => {
    const { brandName } = useParams();
    const { data } = useQueryPageCustom<IBrandDetails>({
        ignoreQuery: true,
        endpoint: "/marka/" + brandName,
    });

    const props = data.component;
    const [state, setState] = useState<IBrandDetailsState>({
        searchText: "",
    });

    const textRef = useRef("");
    const isNotFound = data?.component?.componentName === "NotFound";

    const jsonLdString = useMemo(() => {
        if (!props || isNotFound) {
            return "";
        }

        const jsonLdObject = {
            "@context": "http://schema.org",
            "@type": "Store",
            image: props.brandDetailsHeader?.logo?.logo?.src,
            "@id": props.canonicalUrl,
            name: props.brandDetailsHeader.name,
            url: "",
        };

        if (props.brandDetailsHeader.websiteLink) {
            jsonLdObject.url = props.brandUrl;
        }

        return JSON.stringify(jsonLdObject);
    }, [props, isNotFound]);

    useEffect(() => {
        if (!isNotFound) sendToGTMBrandDetails("brandDetails", props.brandDetailsHeader.seoName);
    }, [props, isNotFound]);

    if (isNotFound) {
        return <NotFound />;
    }

    const suggestedBrandsPanel = () => {
        if (
            props.suggestedBrandsPanel &&
            props.initialData.suggestedBrands &&
            props.initialData.suggestedBrands.length
        ) {
            return (
                <aside className={css.suggestedBrandsPanel}>
                    <Panel {...props.suggestedBrandsPanel} titleElement="h4">
                        <RepeaterV2 initialData={props.initialData.suggestedBrands}>
                            <ItemTemplate>
                                <LogoTile {...props.initialData.suggestedBrands[0]} type="brands" />
                            </ItemTemplate>
                        </RepeaterV2>
                    </Panel>
                </aside>
            );
        }
        if (props.suggestedBrandsPanel && props.initialData.suggestedBrands === null) {
            return (
                <aside className={css.suggestedBrandsPanel}>
                    <Loading />
                </aside>
            );
        }

        return null;
    };

    const onSelect = (_event: React.MouseEvent<HTMLAnchorElement>, item: ISearchBoxResultItem): boolean => {
        if (item && item.text) {
            setState({ ...state, searchText: item.text });
        }

        return true;
    };

    const onValueChange = (event: React.ChangeEvent<HTMLInputElement>): boolean => {
        textRef.current = event.target.value;

        if (textRef.current === "") {
            setState({ ...state, searchText: textRef.current });
        }

        return true;
    };

    const breadcrumbs: IBreadcrumbItem[] = [
        { labelKey: "followedBrands", path: FollowedBrands },
        { label: props.brandDetailsHeader.name },
    ];

    return (
        <PageLayout layoutAdUnitPath="marki" slugTarget={props.canonicalUrl} breadcrumbs={breadcrumbs}>
            <section className={css.brandDetailsComponent}>
                {props.navigation && <DiscountNavigation {...props.navigation} />}
                <div className={portalCss.standardContainer}>
                    <div className={css.brandContent}>
                        <BrandDetailsHeader
                            {...props.brandDetailsHeader}
                            id={props.id}
                            showNewsletterSignupItem={props.initialData.showNewsletterSignupItem}
                            cashbackDetails={props.initialData.cashbackDetails}
                        />

                        <PCPopularBrandOffers brandId={props.id} />

                        {props.brandProductsSearch && (
                            <div className={css.brandSearchBanner}>
                                <div className={css.banner} />
                                <div className={`${css.productsSearch} ${portalCss.standardContainer}`}>
                                    <SearchBox
                                        {...props.brandProductsSearch}
                                        ajaxMethod={AjaxMethod.Post}
                                        value={state.searchText}
                                        onSelect={onSelect}
                                        onChange={onValueChange}
                                        aria-label={props.brandProductsSearch.watermarkText}
                                    />
                                </div>
                            </div>
                        )}

                        {props.flyersNoResults && (
                            <NoResultsTemplate>{props.flyersNoResults}</NoResultsTemplate>
                        )}
                        {props.flyersList && (
                            <div className={css.flyers}>
                                <h2>{props.flyersTitle}</h2>
                                <FlyersSlider {...props.flyersList} searchText={state.searchText} />
                            </div>
                        )}
                        {props.initialData.discounts && (
                            <BrandDetailsContent
                                {...props.brandDetailsContent}
                                discounts={props.initialData.discounts}
                                showOnNoDiscounts={!props.flyersList}
                            />
                        )}
                        {props.appDownloadBanner && (
                            <AppDownloadBanner
                                {...props.appDownloadBanner}
                                className={css.appDownloadBanner}
                            />
                        )}
                        {suggestedBrandsPanel()}
                        <SuggestedFlyers brandId={props.id} clickFrom="FlyersWorld" />
                        {props.allShopsLink && (
                            <TransitionLink {...props.allShopsLink} className={css.allShopsButton} />
                        )}
                    </div>
                    <div>
                        <ContentContainer {...props.contentContainers} />
                    </div>
                </div>
                <Outlet />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdString }} />
            </section>
        </PageLayout>
    );
};
